import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Form } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import './Internship.css';

const Internship = () => {
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phone: '',
    position: '',
    education: '',
    college: '',
    duration: '',
    internshipType: '',
    skills: '',
    resumeLink: '',
    portfolio: '',
    motivation: '',
    availability: ''
  });

  const [submitStatus, setSubmitStatus] = useState({ loading: false, error: '', success: false });

  useEffect(() => {
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, error: '', success: false });

    const message = `Internship Application Details:

Name: ${formData.fullname}
Email: ${formData.email}
Phone: ${formData.phone}
Position: ${formData.position}
Education: ${formData.education}
College: ${formData.college}
Duration: ${formData.duration}
Type: ${formData.internshipType}
Skills: ${formData.skills}
Resume: ${formData.resumeLink}
Portfolio: ${formData.portfolio || 'Not provided'}
Availability: ${formData.availability}

Motivation:
${formData.motivation}`;

    try {
      // EmailJS integration (you'll need to install emailjs-com: npm install emailjs-com)
      // For now, just simulate success
      setTimeout(() => {
        setSubmitStatus({ loading: false, error: '', success: true });
        setFormData({
          fullname: '',
          email: '',
          phone: '',
          position: '',
          education: '',
          college: '',
          duration: '',
          internshipType: '',
          skills: '',
          resumeLink: '',
          portfolio: '',
          motivation: '',
          availability: ''
        });
        
        setTimeout(() => {
          setSubmitStatus({ loading: false, error: '', success: false });
        }, 5000);
      }, 1000);
    } catch (error) {
      setSubmitStatus({ 
        loading: false, 
        error: 'Failed to submit application. Please try again or contact us directly.', 
        success: false 
      });
    }
  };

  return (
    <div className="internship-page">
      <Helmet>
        <title>Internship Program | BHIT Infotech</title>
        <meta name="description" content="Join BHIT Infotech's internship program and gain hands-on experience in software development, data science, and IT consulting. Build real-world skills with industry experts." />
        <meta name="keywords" content="IT internship, software development internship, data science internship, tech internship, BHIT Infotech careers, internship program, web development internship" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Internship Program - BHIT Infotech" />
        <meta property="og:description" content="Launch your tech career with hands-on internship experience at BHIT Infotech" />
        <meta property="og:image" content="https://bhitinfotech.com/assets/images/bhit-logo.png" />
        <meta property="og:url" content="https://bhitinfotech.com/internship" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Internship Program - BHIT Infotech" />
        <meta name="twitter:description" content="Launch your tech career with hands-on internship experience at BHIT Infotech" />
        <meta name="twitter:image" content="https://bhitinfotech.com/assets/images/bhit-logo.png" />
      </Helmet>

      <PublicNavbar />

      {/* Page Title */}
      <div className="page-title" style={{ background: 'linear-gradient(135deg, #00428a 0%, #55c900 100%)', padding: '60px 0', color: 'white' }}>
        <Container>
          <h1 className="display-4 fw-bold">BHIT Internship Program</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb justify-content-center mb-0">
              <li className="breadcrumb-item"><a href="/" style={{ color: 'white' }}>Home</a></li>
              <li className="breadcrumb-item active text-white">Internship</li>
            </ol>
          </nav>
        </Container>
      </div>

      {/* Hero Section */}
      <section id="internship-hero" className="section py-5 bg-light">
        <Container>
          <Row className="gy-4 align-items-center">
            <Col lg={6}>
              <h2 className="display-5 fw-bold">Launch Your Tech Career with Real-World Experience</h2>
              <p className="lead">Join BHIT Infotech's comprehensive internship program and work on live projects alongside industry professionals. Gain practical skills, build your portfolio, and kickstart your journey in the tech industry.</p>
              <div className="d-flex gap-3 mt-4">
                <Button href="#positions" style={{ backgroundColor: '#55c900', borderColor: '#55c900' }}>Explore Positions</Button>
                <Button href="#apply" variant="primary" style={{ backgroundColor: '#00428a' }}>Apply Now</Button>
              </div>
            </Col>
            <Col lg={6}>
              <div className="rounded shadow" style={{ height: '400px', background: 'linear-gradient(135deg, rgba(0,66,138,0.1) 0%, rgba(85,201,0,0.1) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/assets/images/about.jpg" alt="BHIT Infotech Internship" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section id="internship-stats" className="stats section py-5">
        <Container>
          <Row className="gy-4 text-center">
            {[
              { number: '200+', label: 'Successful Interns' },
              { number: '15+', label: 'Industry Projects' },
              { number: '85%', label: 'Placement Rate' },
              { number: '6-12', label: 'Weeks Duration' }
            ].map((stat, idx) => (
              <Col lg={3} md={6} key={idx}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <h2 className="display-4 fw-bold" style={{ color: '#55c900' }}>{stat.number}</h2>
                    <p className="text-muted">{stat.label}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="section py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold gradient-text">Why Intern at BHIT Infotech?</h2>
            <p className="lead">Our internship program is designed to provide you with industry-ready skills and real-world experience</p>
          </div>

          <Row className="gy-4">
            {[
              { icon: 'bi-lightbulb', title: 'Hands-On Learning', desc: 'Work on real client projects and cutting-edge technologies under the guidance of experienced mentors.', color: '#17a2b8' },
              { icon: 'bi-people', title: 'Expert Mentorship', desc: 'Learn from industry professionals with years of experience in software development and IT consulting.', color: '#fd7e14' },
              { icon: 'bi-award', title: 'Certificate & LOR', desc: 'Receive an internship completion certificate and Letter of Recommendation upon successful completion.', color: '#20c997' },
              { icon: 'bi-briefcase', title: 'Career Support', desc: 'Get resume building assistance, interview preparation, and job placement support after completion.', color: '#dc3545' },
              { icon: 'bi-calendar-check', title: 'Flexible Schedule', desc: 'Choose between full-time or part-time internships to fit your academic or personal schedule.', color: '#6610f2' },
              { icon: 'bi-graph-up-arrow', title: 'PPO Opportunity', desc: 'High-performing interns may receive Pre-Placement Offers based on their performance and contributions.', color: '#e83e8c' }
            ].map((benefit, idx) => (
              <Col lg={4} md={6} key={idx}>
                <Card className="h-100 border-0 shadow-sm card-hover-lift">
                  <Card.Body>
                    <div className="mb-3">
                      <i className={`bi ${benefit.icon}`} style={{ fontSize: '3rem', color: benefit.color }}></i>
                    </div>
                    <h5 className="fw-bold">{benefit.title}</h5>
                    <p className="text-muted">{benefit.desc}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Positions Section */}
      <section id="positions" className="section py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold gradient-text">Available Internship Positions</h2>
            <p className="lead">Choose the track that aligns with your career goals and interests</p>
          </div>

          <Row className="gy-4">
            {[
              {
                icon: 'bi-code-slash',
                title: 'Full Stack Development',
                desc: 'Build end-to-end web applications using modern frameworks and technologies.',
                techs: ['HTML, CSS, JavaScript', 'React.js / Angular / Vue.js', 'Node.js, Express.js', 'MongoDB / MySQL / PostgreSQL'],
                duration: '6-12 weeks',
                color: '#007bff'
              },
              {
                icon: 'bi-cpu',
                title: 'Python Development',
                desc: 'Work on automation, backend development, and data processing projects.',
                techs: ['Python 3.x', 'Django / Flask / FastAPI', 'REST APIs', 'Automation & Scripting'],
                duration: '6-12 weeks',
                color: '#28a745'
              },
              {
                icon: 'bi-bar-chart-line',
                title: 'Data Science & Analytics',
                desc: 'Analyze data, build predictive models, and create insightful visualizations.',
                techs: ['Python (Pandas, NumPy)', 'Machine Learning (Scikit-learn)', 'Data Visualization (Matplotlib, Seaborn)', 'SQL & Database Management'],
                duration: '8-12 weeks',
                color: '#17a2b8'
              },
              {
                icon: 'bi-palette',
                title: 'UI/UX Design',
                desc: 'Create user-centered designs and engaging interfaces for web and mobile apps.',
                techs: ['Figma / Adobe XD', 'Wireframing & Prototyping', 'User Research & Testing', 'Responsive Design'],
                duration: '6-10 weeks',
                color: '#ffc107'
              },
              {
                icon: 'bi-cloud-arrow-up',
                title: 'Cloud & DevOps',
                desc: 'Learn cloud infrastructure, CI/CD pipelines, and deployment automation.',
                techs: ['AWS / Azure / GCP', 'Docker & Kubernetes', 'CI/CD (Jenkins, GitLab)', 'Infrastructure as Code'],
                duration: '8-12 weeks',
                color: '#dc3545'
              },
              {
                icon: 'bi-megaphone',
                title: 'Digital Marketing',
                desc: 'Execute marketing campaigns and analyze performance across digital channels.',
                techs: ['SEO & SEM', 'Social Media Marketing', 'Content Creation', 'Analytics & Reporting'],
                duration: '6-10 weeks',
                color: '#6c757d'
              }
            ].map((position, idx) => (
              <Col lg={6} key={idx}>
                <Card className="h-100 border-0 shadow-sm card-hover-lift">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle p-3 me-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: position.color, color: 'white', width: '50px', height: '50px', flexShrink: 0 }}>
                        <i className={`bi ${position.icon}`} style={{ fontSize: '1.5rem' }}></i>
                      </div>
                      <h4 className="mb-0">{position.title}</h4>
                    </div>
                    <p className="text-muted mb-3">{position.desc}</p>
                    <h6 className="fw-bold">Key Technologies:</h6>
                    <ul className="list-unstyled">
                      {position.techs.map((tech, i) => (
                        <li key={i}><i className="bi bi-check-circle text-success me-2"></i>{tech}</li>
                      ))}
                    </ul>
                    <p className="text-muted small mb-0"><strong>Duration:</strong> {position.duration}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Application Form Section */}
      <section id="apply" className="section py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold gradient-text">Apply for Internship</h2>
            <p className="lead">Take the first step towards your tech career - fill out the application form below</p>
          </div>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="border-0 shadow">
                <Card.Body className="p-4">
                  <Form onSubmit={handleSubmit}>
                    <Row className="gy-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Full Name *</Form.Label>
                          <Form.Control type="text" name="fullname" value={formData.fullname} onChange={handleChange} placeholder="Your Full Name" required />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Email Address *</Form.Label>
                          <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your.email@example.com" required />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Phone Number *</Form.Label>
                          <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXXXXXXX" required />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Preferred Position *</Form.Label>
                          <Form.Select name="position" value={formData.position} onChange={handleChange} required>
                            <option value="">Select Position</option>
                            <option value="Full Stack Development">Full Stack Development</option>
                            <option value="Python Development">Python Development</option>
                            <option value="Data Science & Analytics">Data Science & Analytics</option>
                            <option value="UI/UX Design">UI/UX Design</option>
                            <option value="Cloud & DevOps">Cloud & DevOps</option>
                            <option value="Digital Marketing">Digital Marketing</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Current Education *</Form.Label>
                          <Form.Control type="text" name="education" value={formData.education} onChange={handleChange} placeholder="e.g., B.Tech CSE 3rd Year" required />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>College/University *</Form.Label>
                          <Form.Control type="text" name="college" value={formData.college} onChange={handleChange} placeholder="Your Institution Name" required />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Preferred Duration *</Form.Label>
                          <Form.Select name="duration" value={formData.duration} onChange={handleChange} required>
                            <option value="">Select Duration</option>
                            <option value="6 weeks">6 weeks</option>
                            <option value="8 weeks">8 weeks</option>
                            <option value="10 weeks">10 weeks</option>
                            <option value="12 weeks">12 weeks</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Internship Type *</Form.Label>
                          <Form.Select name="internshipType" value={formData.internshipType} onChange={handleChange} required>
                            <option value="">Select Type</option>
                            <option value="Full-time">Full-time (40 hrs/week)</option>
                            <option value="Part-time">Part-time (20 hrs/week)</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Technical Skills *</Form.Label>
                          <Form.Control type="text" name="skills" value={formData.skills} onChange={handleChange} placeholder="e.g., Python, JavaScript, React, SQL" required />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Resume Link (Google Drive / Dropbox) *</Form.Label>
                          <Form.Control type="url" name="resumeLink" value={formData.resumeLink} onChange={handleChange} placeholder="https://drive.google.com/..." required />
                          <Form.Text className="text-muted">Please ensure the link has viewing permission enabled</Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Portfolio / GitHub Link (Optional)</Form.Label>
                          <Form.Control type="url" name="portfolio" value={formData.portfolio} onChange={handleChange} placeholder="https://github.com/yourusername" />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Why do you want to intern at BHIT Infotech? *</Form.Label>
                          <Form.Control as="textarea" rows={4} name="motivation" value={formData.motivation} onChange={handleChange} placeholder="Tell us about your motivation and what you hope to learn..." required />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>When can you start? *</Form.Label>
                          <Form.Control type="date" name="availability" value={formData.availability} onChange={handleChange} required />
                        </Form.Group>
                      </Col>

                      <Col md={12} className="text-center mt-4">
                        {submitStatus.loading && <div className="alert alert-info">Submitting application...</div>}
                        {submitStatus.error && <div className="alert alert-danger">{submitStatus.error}</div>}
                        {submitStatus.success && <div className="alert alert-success">Your application has been submitted successfully! We'll contact you soon.</div>}
                        <Button type="submit" size="lg" style={{ backgroundColor: '#55c900', borderColor: '#55c900', minWidth: '200px' }} disabled={submitStatus.loading}>
                          {submitStatus.loading ? 'Submitting...' : 'Submit Application'}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="call-to-action section py-5 text-white" style={{ background: 'linear-gradient(135deg, #00428a 0%, #55c900 100%)' }}>
        <Container>
          <Row className="justify-content-center">
            <Col xl={10} className="text-center">
              <h2 className="display-5 fw-bold mb-4">Ready to Start Your Tech Journey?</h2>
              <p className="lead mb-4">Don't miss this opportunity to gain real-world experience and launch your career in technology. Apply now and take the first step towards your future!</p>
              <Button href="#apply" size="lg" variant="light" style={{ backgroundColor: 'white', color: '#00428a', fontWeight: 'bold' }}>Apply for Internship</Button>
            </Col>
          </Row>
        </Container>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Internship;
